import {
	Button,
	ChipSelect,
	DetailPanelNavigation,
	EmptyState,
	Modal,
	ModalContent,
	ModalTitle,
	ResourceOptions,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ondit/ui";
import { Library } from "@ondit/ui/icons";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useAccess, useExitAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type Task, type TaskFilters } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Hint, PageHead } from "~/core/ui/page";
import { TableSkeleton } from "~/core/ui/skeletons";
import { Async } from "~/core/ui/states";
import { AgentRuns } from "~/modules/agent/runs";
import { ScheduledTasks } from "~/modules/agent/schedules";
import { AgentInbox } from "~/modules/analytics/components/agent-inbox";
import { useAgentInbox } from "~/modules/analytics/lib/agent-inbox";
import { GENERATION_CHANGED } from "~/modules/analytics/lib/generation-tracking";
import { canPause, canResume, TASK_KINDS, TASK_STATUSES, taskTarget } from "~/modules/analytics/lib/tasks";

function moment(locale: string, at: string | null) {
	return at
		? new Date(at).toLocaleString(locale, {
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
				hour12: false,
			})
		: "—";
}
/// What a running task last reported: the unit it is working through, and which
/// unit that is. Nothing while it is queued, and nothing once it has ended.
function progressText(task: Task): string | null {
	const reading = task.progress;
	if (!reading || reading.total <= 0) return null;
	return `${reading.done} / ${reading.total}${reading.note ? ` · ${reading.note}` : ""}`;
}

function statusClass(status: string) {
	return `pill${status === "failed" ? " pill-danger" : status === "done" ? " pill-success" : status === "skipped" ? " pill-warning" : ""}`;
}

/**
 * What a paused task says it is.
 *
 * The mark is not a status — the row is still queued, or still settling the
 * answers it had already bought — but "queued" beside a Resume button is the
 * screen contradicting itself, so the pill reads the mark first.
 */
function TaskStatus({ task }: { task: Task }) {
	const t = useT();
	const paused = task.paused_at ? (task.status === "running" ? "pausing" : "paused") : null;
	return (
		<span className={paused ? "pill pill-warning" : statusClass(task.status)}>
			{t(paused ? `tasks.${paused}` : `tasks.${task.status}`)}
		</span>
	);
}

/**
 * Stop a task, or start it again.
 *
 * Pausing is an exit — it is how an account stops spending — so it survives a
 * lapsed plan; resuming buys answers again and asks for the editor the rest of
 * the product asks for. The server decides either way; these disable so a
 * button that would be refused is not offered.
 */
function TaskControls({ task, onMoved }: { task: Task; onMoved: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	const exit = useExitAccess();
	const { editor, why } = useAccess();
	const [busy, setBusy] = useState(false);
	const resumable = canResume(task);
	if (!resumable && !canPause(task)) return null;
	const allowed = resumable ? editor : exit.editor;
	async function move() {
		setBusy(true);
		try {
			await (resumable ? api.resumeTask(task.id) : api.pauseTask(task.id));
			onMoved();
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}
	return (
		<Button
			variant="default"
			disabled={busy || !allowed}
			title={whyDisabled(allowed, why.editor)}
			onClick={() => void move()}
		>
			{t(resumable ? "tasks.resume" : "tasks.pause")}
		</Button>
	);
}
/** Every five seconds while the tab is in view, the query's own interval. */
const REFRESH_MS = 5000;

/** And at once when a generation starts or ends here, or the tab comes back. */
function useRefresh(reload: () => unknown) {
	useEffect(() => {
		const refresh = () => {
			if (!document.hidden) void reload();
		};
		window.addEventListener(GENERATION_CHANGED, refresh);
		document.addEventListener("visibilitychange", refresh);
		return () => {
			window.removeEventListener(GENERATION_CHANGED, refresh);
			document.removeEventListener("visibilitychange", refresh);
		};
	}, [reload]);
}

/**
 * Everything that runs on the account's behalf: the loop's jobs, and the
 * Agent's runs.
 *
 * One screen for both modules, because somebody
 * asking whether a thing ran should not have to know which module ran it. The
 * Agent's tab is its own run history, mounted unchanged, rather than the
 * `agent.turn` and `agent.workflow` jobs those runs ride on: one turn can run
 * several workflows, so a job is the wrong unit to read a run by, and what a
 * run is read for is its nodes.
 */
export function TaskCenter({
	orgId,
	brandId,
	brands,
	agent = true,
}: {
	orgId: string;
	brandId: string;
	brands: { entity_id: string; name: string }[];
	/**
	 * Whether the account's plan includes the Agent. Three of the four tabs
	 * here are its — the approvals, the run history and the schedules — so a
	 * plan without it has one list rather than a tab strip with three tabs that
	 * refuse.
	 */
	agent?: boolean;
}) {
	const t = useT();
	const navigate = useNavigate();
	const rest = useParams()["*"];
	const tab = rest === "scheduled" ? "scheduled" : rest === "agent" ? "agent" : rest === "inbox" ? "inbox" : "loop";
	const inbox = useAgentInbox(orgId, agent);
	const waiting = inbox.data?.items.length ?? 0;
	if (!agent) {
		return (
			<div className="fills-pane">
				<PageHead icon={<Library />} title={t("tasks.title")} lede={t("tasks.scope")} />
				<LoopTasks orgId={orgId} brandId={brandId} brands={brands} />
			</div>
		);
	}
	return (
		/* The screen fills the pane rather than growing with its content: the
		   Agent's run history is a virtualized table that needs a height, and so
		   does the detail panel it opens beside itself. The two tabs that are
		   ordinary lists scroll inside it. */
		<div className="fills-pane">
			<PageHead icon={<Library />} title={t("tasks.title")} lede={t("tasks.scope")} />
			<Tabs
				value={tab}
				onValueChange={(next) => navigate(`/w/${brandId}/tasks${next === "loop" ? "" : `/${next}`}`)}
				className="flex min-h-0 flex-1 flex-col gap-(--s-10)"
			>
				<TabsList aria-label={t("tasks.title")}>
					{/*
					 * First, and the only one carrying a count: it is the only list
					 * here that is waiting on the reader rather than reporting to
					 * them. It was a section above the tabs, always drawn — which put
					 * an empty table on the screen most days and pushed what somebody
					 * came to read below it.
					 */}
					<TabsTrigger value="inbox">
						{t("tasks.inbox")}
						{waiting ? ` · ${waiting}` : ""}
					</TabsTrigger>
					<TabsTrigger value="loop">{t("tasks.tabLoop")}</TabsTrigger>
					<TabsTrigger value="agent">{t("tasks.tabAgent")}</TabsTrigger>
					<TabsTrigger value="scheduled">{t("tasks.tabScheduled")}</TabsTrigger>
				</TabsList>
				<TabsContent value={tab} className="flex min-h-0 flex-1 flex-col overflow-auto">
					{tab === "scheduled" ? (
						<Suspense fallback={<TableSkeleton />}>
							<ScheduledTasks brandId={brandId} />
						</Suspense>
					) : tab === "inbox" ? (
						<AgentInbox org={orgId} />
					) : tab === "agent" ? (
						<Suspense fallback={<TableSkeleton />}>
							<AgentRuns />
						</Suspense>
					) : (
						<LoopTasks orgId={orgId} brandId={brandId} brands={brands} />
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}

function LoopTasks({
	orgId,
	brandId,
	brands,
}: {
	orgId: string;
	brandId: string;
	brands: { entity_id: string; name: string }[];
}) {
	const t = useT();
	const [params, setParams] = useSearchParams();
	// The brand being read unless somebody asks for the whole account: the
	// Agent's tab is one brand's, and the two tabs should open on the same one.
	const chosen = params.get("brand") ?? brandId;
	const filter: TaskFilters = {
		entity: chosen === "all" ? undefined : chosen,
		kind: params.get("kind") || undefined,
		status: params.get("status") || undefined,
		search: params.get("q") || undefined,
		days: Number(params.get("days") ?? 7),
		page: Math.max(0, Number(params.get("page")) || 0),
	};
	const state = useQuery({
		queryKey: [...keys.org(orgId), "tasks", filter],
		queryFn: () => api.tasks(orgId, filter),
		...read,
		refetchInterval: REFRESH_MS,
	});
	useRefresh(state.refetch);
	const change = (key: string, value: string) => {
		setParams(
			(previous) => {
				const next = new URLSearchParams(previous);
				if (value) next.set(key, value);
				else next.delete(key);
				if (key !== "page" && key !== "task") next.delete("page");
				return next;
			},
			{ replace: true },
		);
	};
	return (
		<>
			<Tabs value={filter.status || "all"} onValueChange={(status) => change("status", status === "all" ? "" : status)}>
				<div className="mb-(--s-6) flex flex-wrap items-center gap-(--s-6)">
					<TabsList aria-label={t("table.status")} className="max-w-full shrink-0 overflow-x-auto">
						{filter.status === "active" ? (
							<TabsTrigger value="active" className="whitespace-nowrap">
								{t("tasks.active")} {state.data ? state.data.counts.running + state.data.counts.queued : "—"}
							</TabsTrigger>
						) : null}
						{["", ...TASK_STATUSES].map((status) => (
							<TabsTrigger key={status} value={status || "all"} className="whitespace-nowrap">
								{t(status ? `tasks.${status}` : "tasks.all")}
								{status ? ` ${state.data?.counts[status as Task["status"]] ?? "—"}` : ""}
							</TabsTrigger>
						))}
					</TabsList>
					<ResourceOptions
						translate={t}
						className="ml-auto w-(--toolbar-search-w) max-w-full shrink-0 border-0 px-0 py-0 [&>div]:flex-nowrap"
						search={{
							value: filter.search ?? "",
							onChange: (value) => change("q", value),
							placeholder: t("tasks.search"),
						}}
						filter={{
							active: Boolean(filter.kind || chosen !== brandId || filter.days !== 7),
							content: (
								<div className="analytics-filter-fields">
									<ChipSelect
										aria-label={t("runs.brand")}
										value={chosen}
										onChange={(value) => change("brand", value)}
										options={[
											{ value: "all", label: t("tasks.allBrands") },
											...brands.map((brand) => ({ value: brand.entity_id, label: brand.name })),
										]}
										searchable
										searchPlaceholder={t("runs.brand")}
										align="start"
									/>
									<ChipSelect
										aria-label={t("tasks.type")}
										value={filter.kind ?? ""}
										placeholder={t("tasks.type")}
										onChange={(value) => change("kind", value)}
										options={[
											{ value: "", label: t("tasks.allTypes") },
											...TASK_KINDS.map((kind) => ({ value: kind, label: t(`jobs.${kind}`) })),
										]}
										align="start"
									/>
									<ChipSelect
										aria-label={t("tasks.period")}
										value={String(filter.days)}
										onChange={(value) => change("days", value)}
										options={[1, 7, 30, 0].map((days) => ({
											value: String(days),
											label: t(days ? "tasks.days" : "tasks.allTime", { n: days }),
										}))}
										align="start"
									/>
								</div>
							),
						}}
					/>
				</div>
				<TabsContent value={filter.status || "all"}>
					<Async state={state}>
						{(data) => (
							<>
								<TaskTable tasks={data.rows} onOpen={(id) => change("task", id)} onMoved={() => void state.refetch()} />
								<div className="task-pagination">
									<span className="faint">
										{t("tasks.total", { n: data.total })} ·{" "}
										{t("tasks.page", { n: data.page + 1, total: Math.max(1, Math.ceil(data.total / data.page_size)) })}
									</span>
									<Button
										variant="default"
										disabled={data.page === 0}
										onClick={() => change("page", String(data.page - 1))}
									>
										{t("tasks.previous")}
									</Button>
									<Button
										variant="default"
										disabled={(data.page + 1) * data.page_size >= data.total}
										onClick={() => change("page", String(data.page + 1))}
									>
										{t("tasks.next")}
									</Button>
								</div>
							</>
						)}
					</Async>
				</TabsContent>
			</Tabs>
			{params.get("task") ? (
				<TaskDrawer
					orgId={orgId}
					id={params.get("task") as string}
					rows={state.data?.rows ?? []}
					onSelect={(id) => change("task", id)}
					onClose={() => change("task", "")}
				/>
			) : null}
		</>
	);
}

function TaskTable({ tasks, onOpen, onMoved }: { tasks: Task[]; onOpen: (id: string) => void; onMoved: () => void }) {
	const t = useT();
	const { locale } = useI18n();
	if (!tasks.length) return <EmptyState size="compact" title={t("tasks.empty")} />;
	return (
		<div className="table-scroll bleed-gutter">
			<Table
				variant="resource"
				className="record-table max-[720px]:[&_td]:p-0 max-[720px]:[&_td]:whitespace-normal max-[720px]:[&_td.num]:text-left"
			>
				<TableHeader>
					<TableRow>
						<TableHead className="wrap">{t("tasks.name")}</TableHead>
						<TableHead>{t("tasks.type")}</TableHead>
						<TableHead>{t("table.status")}</TableHead>
						<TableHead>{t("tasks.started")}</TableHead>
						<TableHead>{t("tasks.finished")}</TableHead>
						<TableHead />
					</TableRow>
				</TableHeader>
				<TableBody>
					{tasks.map((task) => (
						<TableRow key={task.id}>
							<TableCell className="wrap">
								<Button
									variant="link"
									className="task-title h-auto justify-start whitespace-normal px-0 text-left"
									onClick={() => onOpen(task.id)}
								>
									{task.title ?? t(`jobs.${task.kind}`)}
								</Button>
								<div className="faint">
									{task.brand ?? t("tasks.accountTask")}
									{task.engine ? ` · ${task.engine}` : ""}
									{task.locale ? ` · ${task.locale}` : ""}
								</div>
							</TableCell>
							<TableCell data-label={t("tasks.type")}>{t(`jobs.${task.kind}`)}</TableCell>
							<TableCell data-label={t("table.status")}>
								<TaskStatus task={task} />
								{progressText(task) ? <div className="faint mono">{progressText(task)}</div> : null}
								{task.status === "queued" && new Date(task.run_after).getTime() > Date.now() ? (
									<div className="faint">{t("tasks.scheduledFor", { time: moment(locale, task.run_after) })}</div>
								) : null}
								{task.error_code || task.gate_reason ? (
									<div className="faint task-reason">
										<TaskReason task={task} />
									</div>
								) : null}
							</TableCell>
							<TableCell className="mono" data-label={t("tasks.started")}>
								<time title={task.started_at ? new Date(task.started_at).toLocaleString(locale) : t("tasks.noStart")}>
									{moment(locale, task.started_at)}
								</time>
							</TableCell>
							<TableCell className="mono" data-label={t("tasks.finished")}>
								<time title={task.finished_at ? new Date(task.finished_at).toLocaleString(locale) : ""}>
									{moment(locale, task.finished_at)}
								</time>
							</TableCell>
							<TableCell>
								<div className="record-actions">
									<TaskControls task={task} onMoved={onMoved} />
									<Button variant="default" onClick={() => onOpen(task.id)}>
										{t("tasks.details")}
									</Button>
									{taskTarget(task) ? (
										<Button asChild variant="link">
											<Link to={taskTarget(task) as string}>{t("tasks.result")}</Link>
										</Button>
									) : null}
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
function TaskReason({ task }: { task: Task }) {
	const t = useT();
	return task.gate_reason
		? t(`errors.gated.${task.gate_reason}`)
		: task.error_code === "worker_lost"
			? t("tasks.workerLost")
			: task.error_code
				? t(`errors.${task.error_code}`)
				: "—";
}
function TaskDrawer({
	orgId,
	id,
	rows,
	onSelect,
	onClose,
}: {
	orgId: string;
	id: string;
	rows: Task[];
	onSelect: (id: string) => void;
	onClose: () => void;
}) {
	const index = rows.findIndex((row) => row.id === id);
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.org(orgId), "task", id],
		queryFn: () => api.taskDetail(id),
		...read,
		refetchInterval: REFRESH_MS,
	});
	useRefresh(state.refetch);
	return (
		<Modal
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
		>
			<ModalContent side="right" aria-describedby={undefined}>
				<div className="detail-panel-head">
					<ModalTitle className="detail-panel-title">{t("tasks.details")}</ModalTitle>
					<DetailPanelNavigation
						previousLabel={t("detail.previous")}
						nextLabel={t("detail.next")}
						onPrevious={index > 0 ? () => onSelect(rows[index - 1].id) : undefined}
						onNext={index >= 0 && index < rows.length - 1 ? () => onSelect(rows[index + 1].id) : undefined}
					/>
					<Button variant="ghost" size="icon" aria-label={t("common.close")} onClick={onClose}>
						<X />
					</Button>
				</div>
				<div key={id} className="detail-panel-body">
					<Async state={state}>
						{({ task, events }) => (
							<>
								<h2>{task.title ?? t(`jobs.${task.kind}`)}</h2>
								<p className="flex items-center gap-(--s-5)">
									<TaskStatus task={task} />
									<TaskControls task={task} onMoved={() => void state.refetch()} />
								</p>
								<dl className="task-facts">
									{[
										[t("tasks.id"), task.id],
										[t("tasks.type"), t(`jobs.${task.kind}`)],
										[t("runs.brand"), task.brand ?? t("tasks.accountTask")],
										[t("tasks.created"), moment(locale, task.created_at)],
										[t("tasks.started"), moment(locale, task.started_at)],
										[t("tasks.finished"), moment(locale, task.finished_at)],
										[t("tasks.attempts"), `${task.attempts} / ${task.max_attempts}`],
										[t("tasks.scheduled"), moment(locale, task.run_after)],
										...(progressText(task) ? [[t("tasks.progress"), progressText(task) as string]] : []),
									].map(([label, value]) => (
										<div key={label}>
											<dt>{label}</dt>
											<dd>{value}</dd>
										</div>
									))}
								</dl>
								{task.sampling_prompts?.length ? (
									<section>
										<div className="title-row">
											<h3>{t("manual.taskPrompts", { n: task.sampling_prompts.length })}</h3>
											<Hint text={t("manual.snapshotHelp")} />
										</div>
										<ul>
											{task.sampling_prompts.map((prompt) => (
												<li key={prompt.id}>{prompt.text}</li>
											))}
										</ul>
									</section>
								) : null}
								{!task.started_at ? <p className="faint">{t("tasks.noStart")}</p> : null}
								{task.error_code || task.gate_reason ? (
									<section>
										<h3>{t("tasks.reason")}</h3>
										<p>
											<TaskReason task={task} />
										</p>
										{task.error_detail ? <p className="task-error">{task.error_detail}</p> : null}
									</section>
								) : null}
								{taskTarget(task) ? (
									<Button asChild variant="default">
										<Link onClick={onClose} to={taskTarget(task) as string}>
											{t("tasks.openResult")}
										</Link>
									</Button>
								) : null}
								<section>
									<h3>{t("tasks.timeline")}</h3>
									<ol className="task-timeline">
										<li>
											<time>{moment(locale, task.created_at)}</time> {t("tasks.created")}
										</li>
										{events.map((event) => (
											<li key={`${event.at}-${event.code}`}>
												<time>{moment(locale, event.at)}</time>{" "}
												{event.code === "task.started"
													? t("tasks.attemptStarted", { n: event.detail?.attempt ?? "—" })
													: t(`errors.${event.detail?.code ?? "internal"}`)}
											</li>
										))}
										{task.finished_at ? (
											<li>
												<time>{moment(locale, task.finished_at)}</time> {t(`tasks.${task.status}`)}
											</li>
										) : null}
									</ol>
								</section>
							</>
						)}
					</Async>
				</div>
			</ModalContent>
		</Modal>
	);
}
